import { sourceFiles, sourceNames, type ModelDef } from '@/state/models';
import type { PeakData } from '@/ui/waveform';

export interface LoadedModel {
  buffers: AudioBuffer[];   // engine order: sourceNames(model)
  duration: number;
  peaks: PeakData;          // keyed by source name
  peakParseMs: number;
  peakBytes: number;
}

export class LoadError extends Error {
  constructor(
    public readonly reason: 'fetch' | 'decode' | 'peaks' | 'unknown',
    public readonly detail: string,
  ) {
    super(`${reason}: ${detail}`);
    this.name = 'LoadError';
  }
}

function joinUrl(base: string, path: string): string {
  const trimmed = base.endsWith('/') ? base : `${base}/`;
  return `${trimmed}${path}`;
}

interface PeaksFile {
  version: number;
  columns: number;
  stems: Record<string, string>;
}

function base64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function fetchPeaks(
  url: string,
  signal: AbortSignal | undefined,
): Promise<{ peaks: PeakData; parseMs: number; bytes: number }> {
  let res: Response;
  try {
    res = await fetch(url, { signal });
  } catch (err) {
    throw new LoadError('peaks', `network error loading peaks: ${String(err)}`);
  }
  if (!res.ok) throw new LoadError('peaks', `HTTP ${res.status} on ${url}`);

  const text = await res.text();
  const bytes = text.length;
  const t0 = performance.now();
  let file: PeaksFile;
  try {
    file = JSON.parse(text) as PeaksFile;
  } catch (err) {
    throw new LoadError('peaks', `peaks JSON parse failed: ${String(err)}`);
  }
  if (file.version !== 1) {
    throw new LoadError('peaks', `unsupported peaks version: ${file.version}`);
  }
  const peaks: PeakData = {};
  for (const [name, b64] of Object.entries(file.stems)) {
    peaks[name] = base64ToUint8(b64);
  }
  const parseMs = performance.now() - t0;
  return { peaks, parseMs, bytes };
}

async function fetchAndDecode(
  ctx: AudioContext,
  url: string,
  sourceName: string,
  signal: AbortSignal | undefined,
): Promise<AudioBuffer> {
  let res: Response;
  try {
    res = await fetch(url, { signal });
  } catch (err) {
    throw new LoadError('fetch', `network error loading ${sourceName}: ${String(err)}`);
  }
  if (!res.ok) throw new LoadError('fetch', `HTTP ${res.status} on ${url}`);
  const bytes = await res.arrayBuffer();
  try {
    return await ctx.decodeAudioData(bytes);
  } catch (err) {
    throw new LoadError('decode', `decodeAudioData failed for ${sourceName}: ${String(err)}`);
  }
}

export async function loadModel(
  ctx: AudioContext,
  stemsBaseUrl: string,
  model: ModelDef,
  signal?: AbortSignal,
): Promise<LoadedModel> {
  const modelDir = joinUrl(stemsBaseUrl, `${model.id}/`);
  const names = sourceNames(model);
  const files = sourceFiles(model);

  const audioPromises = files.map((file, i) =>
    fetchAndDecode(ctx, joinUrl(modelDir, file), names[i], signal),
  );
  const peaksPromise = fetchPeaks(joinUrl(modelDir, 'peaks.json'), signal);

  const [buffers, peaksResult] = await Promise.all([
    Promise.all(audioPromises),
    peaksPromise,
  ]);

  for (const name of names) {
    if (!peaksResult.peaks[name]) {
      throw new LoadError('peaks', `peaks.json has no entry for "${name}" in ${model.id}`);
    }
  }

  const duration = Math.max(...buffers.map((b) => b.duration));
  return {
    buffers,
    duration,
    peaks: peaksResult.peaks,
    peakParseMs: peaksResult.parseMs,
    peakBytes: peaksResult.bytes,
  };
}
